import { englishUiComponentTranslators } from "@/lib/i18n/ui-component-translations"
import type { useTranslations } from "next-intl"

type ConversationTimeTranslator = ReturnType<typeof useTranslations<"UiComponents.ConversationTime">>
/**
 * Inbox timestamps in the operator's own timezone (`useAuth().user.timezone`),
 * not the browser's. Clock and calendar day must use the same zone, or a
 * late-evening message renders under the wrong day separator.
 */

import { getTodayInTimezone, utcToDateKeyTz } from '@/lib/date-utils'

/** `11:24 AM` */
export function formatClockTime(iso: string, timezone: string, locale?: string): string {
  return new Date(iso).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  })
}

/** `Sep 2` — a recent date, where the year goes without saying. */
export function formatShortDate(iso: string, timezone: string, locale?: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
  })
}

/** `24 August 2026 at 11:24 AM` — the timestamp tooltip. */
export function formatFullDateTime(iso: string, timezone: string, locale?: string): string {
  return new Date(iso).toLocaleString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  })
}

/** Day separator inside a thread. */
export function formatDayLabel(iso: string, timezone: string, t: ConversationTimeTranslator = englishUiComponentTranslators.ConversationTime, locale = "en"): string {
  const day = utcToDateKeyTz(iso, timezone)
  const { today, yesterday } = surroundingDayKeys(timezone)

  if (day === today) return t("today")
  if (day === yesterday) return t("yesterday")
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  })
}

/** Conversation-list stamp: time today, then `Yesterday`, weekday, finally a date. */
export function formatListTimestamp(iso: string | null, timezone: string, t: ConversationTimeTranslator = englishUiComponentTranslators.ConversationTime, locale = "en"): string {
  if (!iso) return ''

  const day = utcToDateKeyTz(iso, timezone)
  const { today, yesterday } = surroundingDayKeys(timezone)

  if (day === today) return formatClockTime(iso, timezone, locale)
  if (day === yesterday) return t("yesterday")

  const at = new Date(iso)
  const withinWeek = Date.now() - at.getTime() < 7 * DAY_MS
  return at.toLocaleDateString(locale, {
    ...(withinWeek ? { weekday: 'short' } : { month: 'short', day: 'numeric' }),
    timeZone: timezone,
  })
}

export function isTodayInZone(iso: string, timezone: string): boolean {
  return utcToDateKeyTz(iso, timezone) === getTodayInTimezone(timezone)
}

const DAY_MS = 24 * 60 * 60 * 1000

function surroundingDayKeys(timezone: string): { today: string; yesterday: string } {
  return {
    today: getTodayInTimezone(timezone),
    yesterday: utcToDateKeyTz(new Date(Date.now() - DAY_MS).toISOString(), timezone),
  }
}

/**
 * Consecutive items sharing a calendar day, for a sticky day header: the header
 * has to wrap its whole group, not sit beside the first item of it.
 *
 * `key` is the React key and must survive the thread window sliding: keyed by its
 * first item, the oldest day remounted whenever that item dropped out, stopping
 * any audio or video playing in it. Out-of-order input can bring a day back, so a
 * repeated day carries its run number.
 */
export function groupByDayInZone<T>(
  items: T[],
  timezone: string,
  at: (item: T) => string,
): Array<{ day: string; key: string; items: T[] }> {
  const groups: Array<{ day: string; key: string; items: T[] }> = []
  const runsByDay = new Map<string, number>()
  for (const item of items) {
    const day = utcToDateKeyTz(at(item), timezone)
    const current = groups.at(-1)
    if (current?.day === day) {
      current.items.push(item)
      continue
    }
    const run = runsByDay.get(day) ?? 0
    runsByDay.set(day, run + 1)
    groups.push({ day, key: run === 0 ? day : `${day}#${run}`, items: [item] })
  }
  return groups
}
